import { useState, useEffect } from 'react';
import { FaPlus, FaChevronRight } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { getCollectionOnce } from '../utils/firestoreOnce';

const DEFAULT_FAQS = [
  { id: '1', questionAr: 'ما هي خدماتكم الأساسية؟', questionEn: 'What are your core services?', answerAr: 'نقدم خدمات متكاملة في تطوير المواقع وتطبيقات الهاتف والتسويق الإلكتروني.', answerEn: 'We provide full services in web development, mobile apps, and digital marketing.', order: 0 },
  { id: '2', questionAr: 'ما هي مدة تنفيذ المشروع؟', questionEn: 'What is the project execution duration?', answerAr: 'تختلف المدة حسب حجم المشروع ومتطلباته، وعادة تتراوح من أسبوعين إلى شهر.', answerEn: 'Duration varies by project size and requirements, usually ranging from 2 to 4 weeks.', order: 1 },
  { id: '3', questionAr: 'ما هي خطوات العمل معكم؟', questionEn: 'What are the steps to work with you?', answerAr: 'تبدأ بدراسة الفكرة، ثم التخطيط، تليها مرحلة التصميم، ثم البرمجة، وأخيراً الإطلاق والاختبار.', answerEn: 'Starts with idea study, then planning, followed by design, development, and finally launch and testing.', order: 2 },
  { id: '4', questionAr: 'هل تقدمون دعماً فنياً بعد التسليم؟', questionEn: 'Do you provide technical support after delivery?', answerAr: 'نعم، نقدم دعماً فنياً مستمراً لضمان عمل مشروعك بكفاءة عالية بدون مشاكل.', answerEn: 'Yes, we provide continuous technical support to ensure your project runs efficiently without issues.', order: 3 },
  { id: '5', questionAr: 'هل توفرون استضافة وحجز دومين؟', questionEn: 'Do you provide hosting and domain registration?', answerAr: 'نعم، نوفر استضافات سريعة وموثوقة مع إمكانية حجز اسم النطاق (الدومين) الخاص بك.', answerEn: 'Yes, we provide fast and reliable hosting with the ability to register your domain name.', order: 4 },
  { id: '6', questionAr: 'هل يمكن تحديث الموقع بعد التسليم؟', questionEn: 'Can the website be updated after delivery?', answerAr: 'بالتأكيد، تم بناء مواقعنا لتكون قابلة للتحديث والتطوير وإضافة ميزات جديدة في أي وقت.', answerEn: 'Certainly, our websites are built to be scalable and updated with new features at any time.', order: 5 },
];

function FAQ() {
  const [openId, setOpenId] = useState(null);
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language === 'ar' ? 'ar' : 'en';
  const isArabic = currentLanguage === 'ar';

  // Show defaults immediately so first paint is instant; replace if Firestore
  // has authored data. We DO NOT auto-write defaults from public visitors —
  // that opened a Firestore Write/channel per visit and was the main source
  // of the production net::ERR_TIMED_OUT noise. Admins seed via the dashboard.
  const [faqs, setFaqs] = useState(DEFAULT_FAQS);

  useEffect(() => {
    let cancelled = false;
    getCollectionOnce('faqItems')
      .then((snap) => {
        if (cancelled || snap.empty) return;
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        data.sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
        setFaqs(data);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Display max 6 FAQs for the 2-column layout
  const displayedFaqs = faqs.slice(0, 6);
  
  // Split into two columns
  const col1 = displayedFaqs.filter((_, i) => i % 2 === 0);
  const col2 = displayedFaqs.filter((_, i) => i % 2 !== 0);

  const FaqItem = ({ faq }) => (
    <div className="border-b border-white/5 last:border-b-0 overflow-hidden">
      <button
        className="w-full px-5 py-5 flex items-center justify-between hover:bg-white/5 transition-colors group"
        onClick={() => setOpenId(openId === faq.id ? null : faq.id)}
      >
        {/* Right side in RTL (Text & Arrow) */}
        <div className="flex items-center gap-3 flex-1">
          <FaChevronRight className={`text-gray-500 shrink-0 text-xs md:text-sm transition-transform duration-300 ${openId === faq.id ? 'rotate-90 text-[#38BDF8]' : isArabic ? 'rotate-180' : ''}`} />
          <span className={`font-bold text-sm md:text-base text-right leading-relaxed transition-colors duration-300 ${openId === faq.id ? 'text-[#38BDF8]' : 'text-gray-200 group-hover:text-white'}`}>
            {isArabic ? faq.questionAr : faq.questionEn}
          </span>
        </div>
        
        {/* Left side in RTL (+ Icon) */}
        <div className={`w-7 h-7 md:w-8 md:h-8 ml-4 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 ${openId === faq.id ? 'bg-[#38BDF8] text-white shadow-[0_0_10px_#38BDF8]' : 'bg-[#1E293B] border border-white/5 text-gray-400 group-hover:bg-white/10'}`}>
          <FaPlus className={`transition-transform duration-300 text-xs md:text-sm ${openId === faq.id ? 'rotate-45' : ''}`} />
        </div>
      </button>
      
      <div className={`transition-all duration-500 ease-in-out ${openId === faq.id ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'}`}>
         <div className="px-5 md:px-12 pb-5 text-gray-400 text-sm leading-relaxed" style={{ textAlign: isArabic ? 'right' : 'left' }}>
           {isArabic ? faq.answerAr : faq.answerEn}
         </div>
      </div>
    </div>
  );

  return (
    <section className="py-20 relative z-10" dir={isArabic ? 'rtl' : 'ltr'}>
      <div className="container mx-auto px-4">
        
        {/* Header */}
        <div className="text-center mb-16 relative z-10">
          <div className="inline-flex flex-col items-center justify-center relative">
            <h2 className="text-3xl md:text-4xl font-bold text-white tracking-wide mb-4">
              {isArabic ? "الأسئلة الشائعة" : "FAQ"}
            </h2>
            <div className="flex items-center gap-2">
              <span className="w-6 md:w-8 h-0.5 bg-[#FAA617] rounded-full"></span>
              <span className="w-2 h-2 bg-[#0D6EFD] rounded-full shadow-[0_0_8px_#0D6EFD]"></span>
              <span className="w-6 md:w-8 h-0.5 bg-[#FAA617] rounded-full"></span>
            </div>
          </div>
        </div>

        {/* Mobile View: 1 Continuous Column */}
        <div className="lg:hidden max-w-6xl mx-auto bg-[#0B1120] border border-white/5 rounded-2xl overflow-hidden flex flex-col shadow-lg shadow-black/20">
          {displayedFaqs.map(faq => (
            <FaqItem key={faq.id} faq={faq} />
          ))}
        </div>

        {/* Desktop View: 2 Columns */}
        <div className="hidden lg:grid max-w-6xl mx-auto grid-cols-2 gap-8">
          
          {/* Column 1 (Right in RTL) */}
          <div className="bg-[#0B1120] border border-white/5 rounded-2xl overflow-hidden flex flex-col shadow-lg shadow-black/20">
            {col1.map(faq => (
              <FaqItem key={faq.id} faq={faq} />
            ))}
          </div>

          {/* Column 2 (Left in RTL) */}
          <div className="bg-[#0B1120] border border-white/5 rounded-2xl overflow-hidden flex flex-col shadow-lg shadow-black/20">
            {col2.map(faq => (
              <FaqItem key={faq.id} faq={faq} />
            ))}
          </div>

        </div>
      </div>
    </section>
  );
}

export default FAQ;